import React, { useState, useEffect, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { SMTPSettings } from '../../shared/types.js';
import Button from './Button';
import { cloneDeep } from '../utils';

const SMTPSettingsTab: React.FC = () => {
    const { smtpSettings, updateSmtpSettings } = useTimeClock();
    const [localSettings, setLocalSettings] = useState<SMTPSettings>(cloneDeep(smtpSettings));
    const [testRecipient, setTestRecipient] = useState('');
    const [isTesting, setIsTesting] = useState(false);
    const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        setLocalSettings(cloneDeep(smtpSettings));
        setTestRecipient('');
    }, [smtpSettings]);

    const handleUpdate = <K extends keyof SMTPSettings>(key: K, value: SMTPSettings[K]) => {
        setLocalSettings(prev => ({ ...prev, [key]: value }));
    };

    const isDirty = useMemo(() => {
        return JSON.stringify(smtpSettings) !== JSON.stringify(localSettings);
    }, [smtpSettings, localSettings]);

    const handleApply = () => {
        updateSmtpSettings(localSettings);
        setTestMessage({ type: 'success', text: 'SMTP settings applied successfully.' });
    };

    const handleRevert = () => {
        setLocalSettings(cloneDeep(smtpSettings));
        setTestMessage(null);
    };
    
    const handleTestConnection = async () => {
        if (!testRecipient) {
            setTestMessage({ type: 'error', text: 'Please enter a recipient email for the test.' });
            return;
        }
        setIsTesting(true);
        setTestMessage(null);
        try {
            const response = await fetch('/api/smtp/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recipient: testRecipient, settings: localSettings }),
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Failed to send test email.');
            }
            setTestMessage({ type: 'success', text: result.message });
        } catch (error: any) {
            setTestMessage({ type: 'error', text: error.message });
        } finally {
            setIsTesting(false);
        }
    };

    const inputClass = "block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-300";

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl mx-auto text-gray-900 dark:text-white">
            <div className="space-y-6">
                <div>
                    <label htmlFor="smtpHost" className={labelClass}>Host</label>
                    <input type="text" id="smtpHost" value={localSettings.host} onChange={(e) => handleUpdate('host', e.target.value)} className={inputClass} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <label htmlFor="smtpPort" className={labelClass}>Port</label>
                        <input type="number" id="smtpPort" value={localSettings.port} onChange={(e) => handleUpdate('port', parseInt(e.target.value, 10) || 0)} className={inputClass} />
                    </div>
                     <div>
                        <label htmlFor="smtpEncryption" className={labelClass}>Encryption</label>
                        <select id="smtpEncryption" value={localSettings.encryption} onChange={(e) => handleUpdate('encryption', e.target.value as SMTPSettings['encryption'])} className={inputClass}>
                            <option value="none">None</option>
                            <option value="ssl">SSL</option>
                            <option value="tls">TLS</option>
                        </select>
                    </div>
                </div>

                <hr className="dark:border-gray-700"/>

                <div>
                    <label htmlFor="smtpUsername" className={labelClass}>Username</label>
                    <input type="text" id="smtpUsername" value={localSettings.username} onChange={(e) => handleUpdate('username', e.target.value)} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="smtpPassword" className={labelClass}>Password</label>
                    <input type="password" id="smtpPassword" value={localSettings.password || ''} onChange={(e) => handleUpdate('password', e.target.value)} className={inputClass} />
                </div>
                 <div>
                    <label htmlFor="smtpFrom" className={labelClass}>From Address</label>
                    <input type="email" id="smtpFrom" value={localSettings.fromAddress} onChange={(e) => handleUpdate('fromAddress', e.target.value)} className={inputClass} placeholder="notifications@example.com"/>
                </div>
            </div>

             <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                <label htmlFor="testRecipient" className={labelClass}>Test Recipient</label>
                <div className="mt-1 flex gap-2">
                    <input type="email" id="testRecipient" value={testRecipient} onChange={e => setTestRecipient(e.target.value)} className={inputClass} placeholder="test@example.com" />
                    <Button variant="ghost" onClick={handleTestConnection} disabled={isTesting}>
                        {isTesting ? 'Sending...' : 'Test Connection'}
                    </Button>
                </div>
                {testMessage && (
                    <p className={`text-sm mt-2 p-2 rounded-md ${testMessage.type === 'success' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'}`}>
                        {testMessage.text}
                    </p>
                )}
            </div>

            <div className="flex justify-end items-center pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-4">
                    <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                    <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default SMTPSettingsTab;
